import { CdekShipmentDirection } from './cdek-domain'
import {
  isCompletedSellerReturn,
  isFailedReverseDeliveryStatus,
  isMarketReturnInProgress,
} from './order-returns'
import { orderShipment } from './order-helpers'
import type { YMarketOrder, YMarketReturnFact } from './types'
import { YMarketOrderStatus } from './ymarket-domain'

export enum MonthlyReturnState {
  Returning = 'returning',
  Completed = 'completed',
  Failed = 'failed',
}

export interface MonthlyReturnOrder {
  order: YMarketOrder
  state: MonthlyReturnState
  occurredAt: string
}

interface YandexReturnSummary {
  state: MonthlyReturnState
  occurredAt: string
}

function factTimestamp(fact: YMarketReturnFact): number {
  const timestamp = Date.parse(fact.providerUpdatedAt)
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp
}

function latestYandexReturn(
  facts: readonly YMarketReturnFact[],
  shipmentStatus: YMarketReturnFact['shipmentStatus'],
): YMarketReturnFact | null {
  let latest: YMarketReturnFact | null = null
  for (const fact of facts) {
    if (fact.shipmentStatus !== shipmentStatus) continue
    if (latest === null || factTimestamp(fact) > factTimestamp(latest)) latest = fact
  }
  return latest
}

function fbsYandexReturn(order: YMarketOrder): YandexReturnSummary | null {
  if (
    order.status !== YMarketOrderStatus.Cancelled
    || order.programType !== 'FBS'
  ) return null

  const unredeemedReturns = (order.yandexReturns || []).filter(
    (fact) => fact.returnType === 'UNREDEEMED',
  )
  const inTransit = latestYandexReturn(unredeemedReturns, 'IN_TRANSIT')
  if (inTransit) {
    return { state: MonthlyReturnState.Returning, occurredAt: inTransit.providerUpdatedAt }
  }

  const arrived = latestYandexReturn(unredeemedReturns, 'ARRIVED')
  if (arrived) {
    return { state: MonthlyReturnState.Completed, occurredAt: arrived.providerUpdatedAt }
  }

  return null
}

function dateKeyInRiga(value: string): string | null {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Riga',
    year: 'numeric',
    month: '2-digit',
  }).format(date)
}

export function monthlyReturnState(order: YMarketOrder): MonthlyReturnState | null {
  if (order.status !== YMarketOrderStatus.Cancelled) return null
  const yandexReturn = fbsYandexReturn(order)
  if (yandexReturn) return yandexReturn.state

  const shipment = orderShipment(order)
  if (shipment?.direction !== CdekShipmentDirection.Return) return null
  if (isCompletedSellerReturn(shipment)) return MonthlyReturnState.Completed
  if (isFailedReverseDeliveryStatus(shipment.statusCode)) return MonthlyReturnState.Failed
  return isMarketReturnInProgress(order) ? MonthlyReturnState.Returning : null
}

export function returnsForMonth(
  orders: readonly YMarketOrder[],
  monthKey: string,
): MonthlyReturnOrder[] {
  return orders.flatMap((order) => {
    const yandexReturn = fbsYandexReturn(order)
    const state = yandexReturn?.state ?? monthlyReturnState(order)
    if (!state) return []
    const occurredAt = yandexReturn?.occurredAt
      ?? orderShipment(order)?.changedAt
      ?? order.updateDate
      ?? order.creationDate
    if (dateKeyInRiga(occurredAt) !== monthKey) return []
    return [{ order, state, occurredAt }]
  }).sort((left, right) => Date.parse(right.occurredAt) - Date.parse(left.occurredAt))
}

export function currentMonthKeyInRiga(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Riga',
    year: 'numeric',
    month: '2-digit',
  }).format(now)
}
